/**
 * 사흘 — **설비·현장 흔적 증거.**
 *
 * `07_단서_98오브젝트_초안.md` 에서 기존 조각으로 못 덮던 것들이다.
 * HS01(문 표식) · HS12(끌림흔) · HS19(도어록) · FR04(센서등) · WS01(연탄난로) ·
 * PH06(전시 포스터) · EX01+EX06(마을 안내도).
 *
 * **상표 금지가 여기서도 첫 번째 규칙이다.** 특히 도어록이 위험하다 — 실제 제품은
 * 실루엣만으로 제조사가 읽힌다. 원형 다이얼·특유의 곡면 손잡이·가로 슬라이드 커버를
 * 전부 피하고, 어느 회사에나 있는 **세로 직사각 판 + 숫자 격자 + 곧은 바 손잡이**만
 * 쓴다. 센서등도 마찬가지로 무지 원반과 볼록 렌즈뿐이다.
 *
 * 벽·문·천장에 붙는 것은 `hangs + sunk` 다. 원점이 걸이 지점이고 몸통이 y=0 아래로
 * 내려간다 — joint-check 가 그렇게 강제한다.
 */

import * as THREE from 'three';
import { bevelBox, lathe, type MeshWriter } from './kernel';
import { paintGeometry, type Piece } from './pieces';
import { printedFace, rnd, ribbon, stroke } from './saholEvidence';

/* ------------------------------------------------------------------ *
 * 도구
 * ------------------------------------------------------------------ */

/** -Z 를 보는 인쇄면. `PlaneGeometry` 는 +Z 를 보므로 반드시 뒤집는다 */
function facingFront(w: number, h: number) {
  const p = new THREE.PlaneGeometry(w, h);
  p.rotateY(Math.PI);
  return p;
}

/**
 * 이미 그려 둔 그림 한 장을 그대로 붙이는 면.
 *
 * **캔버스로 다시 그리지 않는다.** 마을 안내도는 손으로 그린 원본이 이미 있고
 * (`client/public/maps/base.jpg`) 수첩(`book.ts` 지도 탭)이 그것을 쓴다. 3D 판에
 * 비슷한 그림을 따로 그리면 두 장이 서로 다른 말을 하게 된다 — 실제로 그랬다.
 * 첫 판의 절차 그림은 「조합센터↔한서진 5분」이었는데 정사(03_맵.md 확정표)와
 * 수첩은 **4분**이다. 한 장만 두고 둘 다 그것을 본다.
 */
const texCache = new Map<string, THREE.Texture>();
function imageFace(url: string) {
  let t = texCache.get(url);
  if (!t) {
    t = new THREE.TextureLoader().load(url);
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 8;
    texCache.set(url, t);
  }
  return new THREE.MeshStandardMaterial({
    map: t,
    roughness: 0.86,
    metalness: 0,
    side: THREE.DoubleSide,
    vertexColors: true,
  });
}

/** 위를 보는 인쇄면. rotateX 만 하면 글의 위쪽이 -Z 로 180도 뒤집혀 읽힌다 */
function facingUp(w: number, d: number) {
  const p = new THREE.PlaneGeometry(w, d);
  p.rotateX(-Math.PI / 2);
  p.rotateY(Math.PI);
  return p;
}

function num(
  g: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  px: number,
  color: string,
  align: CanvasTextAlign = 'left',
) {
  g.fillStyle = color;
  g.font = `600 ${px}px "DejaVu Sans", Arial, Helvetica, sans-serif`;
  g.textAlign = align;
  g.textBaseline = 'alphabetic';
  g.fillText(text, x, y);
  g.textAlign = 'left';
}

/** 배경이 투명한 데칼. 사각 판때기로 안 읽히게 가장자리가 알파로 사라진다. */
function decalFace(draw: (g: CanvasRenderingContext2D, w: number, h: number) => void, w: number, h: number) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d')!;
  draw(g, w, h);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return new THREE.MeshStandardMaterial({
    map: tex,
    roughness: 0.92,
    metalness: 0,
    side: THREE.DoubleSide,
    vertexColors: true,
    transparent: true,
    depthWrite: false,
  });
}

/** 종이를 붙들어 둔 마스킹 테이프 한 조각. 살짝 비뚤어야 손으로 붙인 것으로 읽힌다 */
function tape(w: number, h: number, tilt: number) {
  const g = bevelBox(w, h, 0.0006, 0.0002);
  g.rotateZ(tilt);
  paintGeometry(g, '#d8cfae');
  return g;
}

/**
 * 연탄난로의 **난방기구 부분만** 짓는다 — 몸통·화구 문·연통·다리.
 * `coal_stove` 는 재통 안의 탄 종이가 단서라 재통을 늘 함께 놓는데, 관사처럼
 * 단서가 없는 방에 놓으면 그 종이 더미가 순수한 헛다리가 된다. 그래서 난방기구만
 * 필요한 자리를 위해 여기를 떼어 두 조각이 공유한다 — 같은 난로를 두 벌 그리면
 * 한쪽만 고쳐지는 날이 온다.
 */
/**
 * **재받이 받침 함.** (2026-09-09, Chris: 「재받이가 난로 밑에 안 들어가 있음」)
 * 몸통을 8cm 올리고 그 밑에 뒤판·옆판 둘·윗판으로 된 앞이 트인 함을 둔다 — 재받이(폭 0.30·깊이 0.24·
 * 높이 0.055)가 그 안으로 미끄러져 들어가 손잡이만 보인다. `open=false` 면 앞판까지 막힌 굽이다(재받이 없는 변형).
 * 전부 닫힌 상자라 solid-check 를 지난다. 원점은 바닥 가운데 그대로.
 */
const ASH_H = 0.08;
function ashHousing(w: MeshWriter, d: Record<string, number>, open: boolean) {
  const W = 0.34, D = 0.30, T = 0.01;
  w.part('받침 함');
  const top = bevelBox(W, T, D, 0.002);
  top.translate(0, ASH_H - T / 2, 0);
  w.add(top, 'iron', 0.4);
  const back = bevelBox(W, ASH_H, T, 0.002);
  back.translate(0, ASH_H / 2, D / 2 - T / 2);
  w.add(back, 'iron', 0.4);
  for (const sx of [-1, 1] as const) {
    const side = bevelBox(T, ASH_H, D, 0.002);
    side.translate(sx * (W / 2 - T / 2), ASH_H / 2, 0);
    w.add(side, 'iron', 0.4);
  }
  if (!open) {
    const front = bevelBox(W, ASH_H, T, 0.002);
    front.translate(0, ASH_H / 2, -(D / 2 - T / 2));
    w.add(front, 'iron', 0.4);
  }
  void d;
}

function coalStoveHeater(w: MeshWriter, d: Record<string, number>) {
      const bodyTop = d.h * 0.62;

      w.part('몸통');
      const body = lathe(
        [
          [0, 0.035],
          [d.r * 0.86, 0.035],
          [d.r * 0.98, 0.09],
          [d.r, bodyTop - 0.06],
          [d.r * 0.93, bodyTop - 0.012],
          [d.r * 0.99, bodyTop],
          [d.r * 0.5, bodyTop + 0.004],
          [0, bodyTop + 0.004],
        ],
        26,
      );
      // **여기서 두 번 틀렸다.** iron 은 metalness 0.72 인데 카탈로그 씬에 환경맵이 없어
      // 반사할 것이 없다 → 하이라이트를 뺀 전부가 검정이 된다. concrete 로 바꿨더니 이번에는
      // (절차 텍스처 × 슬롯색 #8d8b86 × 정점색) 삼중 곱이라 역시 검정이었다.
      // 난로 몸통은 그을린 도장 철판이다 — 무광·비금속·밝은 바탕의 paint 슬롯만 답이다.
      paintGeometry(body, '#6d6355');
      w.add(body, 'paint', 0.55);

      w.part('상판 화구');
      // 상판 가운데 뚜껑 링 — 연탄을 갈아 넣는 구멍이다
      const ring = lathe(
        [
          [0.052, bodyTop + 0.004],
          [0.058, bodyTop + 0.012],
          [0.052, bodyTop + 0.016],
          [0.046, bodyTop + 0.012],
        ],
        20,
      );
      paintGeometry(ring, '#5b5449');
      w.add(ring, 'paint', 0.3);
      w.movable('화구 뚜껑', () => {
        const lid = lathe(
          [
            [0, bodyTop + 0.005],
            [0.05, bodyTop + 0.005],
            [0.05, bodyTop + 0.012],
            [0.018, bodyTop + 0.014],
            [0.014, bodyTop + 0.02],
            [0, bodyTop + 0.02],
          ],
          20,
        );
        paintGeometry(lid, '#635b50');
        w.add(lid, 'paint', 0.3);
      });

      w.part('화구 문');
      // -Z 를 본다. 살짝 열려 안쪽 어둠이 보인다
      const frame = bevelBox(0.11, 0.085, 0.012, 0.003);
      frame.translate(0, 0.2, -(d.r + 0.004));
      paintGeometry(frame, '#6e675e');
      w.add(frame, 'paint', 0.25);
      w.movable('화구 문', () => {
        const door = bevelBox(0.092, 0.068, 0.007, 0.0025);
        door.translate(0.046, 0, 0);
        door.rotateY(0.42);
        door.translate(-0.046, 0.2, -(d.r + 0.013));
        paintGeometry(door, '#2f2b26');
        w.add(door, 'iron', 0.25);
        const handle = bevelBox(0.008, 0.03, 0.008, 0.002);
        handle.translate(0.088, 0, -0.006);
        handle.rotateY(0.42);
        handle.translate(-0.046, 0.2, -(d.r + 0.013));
        w.add(handle, 'steel', 0.1);
      });
      // 문 아래 통풍 조절판
      for (let i = 0; i < 3; i++) {
        const v = bevelBox(0.058, 0.006, 0.006, 0.0015);
        v.translate(0, 0.128 + i * 0.011, -(d.r + 0.002));
        paintGeometry(v, '#241f1b');
        w.add(v, 'iron', 0.1);
      }

      /**
       * **연통은 벽으로 나가야 한다.** 예전에는 `bodyTop`(0.41)에서 `d.h`(0.66)까지
       * 25cm 만 서다가 **허공에서 뚝 끊겼다** — 연기가 갈 곳이 없는 난로였다.
       * 실제 연탄난로는 1.3~1.6m 올라간 뒤 팔꿈치로 꺾여 벽이나 창으로 빠진다.
       * 조각은 방 천장 높이를 모르므로 천장까지 직선으로 올리는 대신 꺾는다 —
       * 난로는 등(+Z)을 벽에 붙여 놓는 규칙이므로 꺾이는 방향은 +Z 다.
       */
      const flueH = 1.42;
      const elbowR = 0.11;
      w.part('연통');
      const pipe = lathe(
        [
          [0, bodyTop + 0.004],
          [0.038, bodyTop + 0.004],
          [0.038, flueH - elbowR],
          [0, flueH - elbowR],
        ],
        18,
      );
      paintGeometry(pipe, '#8d867c');
      w.add(pipe, 'paint', 0.4);
      // 팔꿈치 — 네 토막으로 돌려 곡선을 만든다. 한 토막이면 각진 ㄱ 자다
      for (let i = 0; i < 4; i++) {
        const a0 = (i / 4) * (Math.PI / 2);
        const a1 = ((i + 1) / 4) * (Math.PI / 2);
        const am = (a0 + a1) / 2;
        const seg = new THREE.CylinderGeometry(0.038, 0.038, elbowR * 0.82, 12);
        seg.rotateX(am);
        seg.translate(
          0,
          flueH - elbowR + Math.sin(am) * elbowR,
          elbowR - Math.cos(am) * elbowR,
        );
        paintGeometry(seg, '#8d867c');
        w.add(seg, 'paint', 0.4);
      }
      // 벽으로 빠지는 수평 구간
      const out = new THREE.CylinderGeometry(0.038, 0.04, 0.5, 12);
      out.rotateX(Math.PI / 2);
      out.translate(0, flueH, elbowR + 0.25);
      paintGeometry(out, '#8d867c');
      w.add(out, 'paint', 0.4);
      // 연통이 상판 뒤쪽에서 올라가 굽는 것이 아니라 곧게 서므로, 이음 밴드로 마디를 준다
      for (const y of [bodyTop + 0.16, bodyTop + 0.52, bodyTop + 0.88]) {
        const band = lathe(
          [
            [0.038, y],
            [0.044, y + 0.003],
            [0.044, y + 0.013],
            [0.038, y + 0.016],
          ],
          18,
        );
        paintGeometry(band, '#767066');
        w.add(band, 'paint', 0.15);
      }

      w.part('다리');
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2 + 0.5;
        const leg = bevelBox(0.018, 0.042, 0.018, 0.003);
        leg.rotateY(-a);
        leg.translate(Math.sin(a) * d.r * 0.84, 0.021, Math.cos(a) * d.r * 0.84);
        paintGeometry(leg, '#7a7268');
        w.add(leg, 'paint', 0.1);
        const foot = bevelBox(0.042, 0.009, 0.042, 0.002);
        foot.rotateY(-a);
        foot.translate(Math.sin(a) * d.r * 0.9, 0.0045, Math.cos(a) * d.r * 0.9);
        paintGeometry(foot, '#6b645b');
        w.add(foot, 'paint', 0.1);
      }

}

/**
 * 식은 재 표면 — **가루는 형태가 아니라 재질이다.**
 *
 * 구를 흩뿌리거나 격자를 흔들어 만들면 자갈·헝겊이 된다(2026-09-08 두 번 실패).
 * 한 장의 캔버스에서 색과 요철을 **같은 원인**으로 뽑는다: 굵은 얼룩(덜 탄 자리·더 탄 자리) 위에
 * 잔 알갱이를 얹고, 그 밝기를 그대로 bump 로 쓴다. 무광(거칠기 0.97)이라 빛을 되쏘지 않는다.
 */
function ashSurface() {
  const N = 256;
  const c = document.createElement('canvas');
  c.width = N;
  c.height = N;
  const g = c.getContext('2d')!;
  // **연탄재는 탁하고 어둡다.** 밝은 회색은 눈이 된다. 바탕부터 낮게 잡는다
  g.fillStyle = '#4b4f52';
  g.fillRect(0, 0, N, N);
  // 굵은 얼룩: 덜 탄 자리(밝음)와 더 탄 자리(어둠). 크기를 섞어야 한 종류 무늬가 안 된다
  for (let i = 0; i < 34; i++) {
    const x = rnd(i * 3.7) * N, y = rnd(i * 8.1) * N, r = 8 + rnd(i * 5.3) * 40;
    const grd = g.createRadialGradient(x, y, 0, x, y, r);
    const dark = rnd(i * 9.1) < 0.5;
    grd.addColorStop(0, dark ? 'rgba(28,26,24,0.5)' : 'rgba(128,131,134,0.32)');
    grd.addColorStop(1, 'rgba(75,79,82,0)');
    g.fillStyle = grd;
    g.beginPath();
    g.arc(x, y, r, 0, Math.PI * 2);
    g.fill();
  }
  // **가장자리 검댕.** 벽 밑에서 재로 번져 내려온 것. 그을음 판과 재가 따로 놀던 원인이
  // 이게 없어서였다 — 텍스처 네 변을 검게 안쪽으로 그라데이션한다
  for (const [x0, y0, x1] of [[0, 0, N], [0, N, N], [0, 0, 0], [N, 0, N]] as const) {
    const vertical = x0 === x1;
    const grd = vertical
      ? g.createLinearGradient(x0, 0, x0 === 0 ? 34 : N - 34, 0)
      : g.createLinearGradient(0, y0, 0, y0 === 0 ? 34 : N - 34);
    grd.addColorStop(0, 'rgba(18,16,15,0.82)');
    grd.addColorStop(0.55, 'rgba(18,16,15,0.28)');
    grd.addColorStop(1, 'rgba(18,16,15,0)');
    g.fillStyle = grd;
    if (vertical) g.fillRect(x0 === 0 ? 0 : N - 34, 0, 34, N);
    else g.fillRect(0, y0 === 0 ? 0 : N - 34, N, 34);
  }
  // 잔 알갱이 — 1px 단위. 이것이 「가루」를 만든다
  const img = g.getImageData(0, 0, N, N);
  const px = img.data;
  for (let i = 0; i < px.length; i += 4) {
    const n = Math.abs((Math.sin(i * 12.9898) * 43758.5453) % 1);
    const d2 = (n - 0.5) * 22;
    px[i] = Math.max(0, Math.min(255, px[i] + d2));
    px[i + 1] = Math.max(0, Math.min(255, px[i + 1] + d2));
    px[i + 2] = Math.max(0, Math.min(255, px[i + 2] + d2));
  }
  g.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  // **타일하지 않는다.** 가장자리 검댕은 받이 한 장에 한 번만 있어야 한다 — 반복하면 격자 검댕이 된다
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  const bump = new THREE.CanvasTexture(c);
  bump.wrapS = bump.wrapT = THREE.ClampToEdgeWrapping;
  return new THREE.MeshStandardMaterial({
    map: tex,
    bumpMap: bump,
    bumpScale: 0.0016,
    roughness: 0.97,
    metalness: 0,
    vertexColors: true,
  });
}

export const SAHOL_FIXTURE_PIECES: Piece[] = [
  /* ---------------------------------------------------------------- *
   * HS01 — 침실 문 「마감 중 · 긴급상황 외 출입 금지」 표식
   * ---------------------------------------------------------------- */
  {
    id: 'door_sign',
    name: '출입 금지 표식',
    collection: 'sahol',
    category: '단서',
    note:
      'HS01. 침실 문에 붙은 A5 표식. **이 종이 한 장이 사흘 동안 아무도 문을 안 연 이유다** — ' +
      '잠긴 방이 아니라 「작업 중이니 건드리지 말라」는 습관이 방을 잠갔다. 그래서 새 종이가 ' +
      '아니라 **오래 붙어 있던 종이**로 보여야 한다: 테이프는 위 두 귀에만 남았고 아래 두 귀는 ' +
      '떨어져 앞으로 말려 들떠 있다. 원점이 붙인 지점이라 종이가 y=0 아래로 내려간다(hangs + sunk).',
    hangs: true,
    sunk: true,
    dims: { w: 0.148, h: 0.21 },
    clusters: 1,
    build(w, d) {
      // 아래 귀가 떨어져 앞으로 말린다. 종이를 가로 띠 여섯 장으로 쪼개 아래로 갈수록
      // 앞(-Z)으로 더 나오게 한다 — 한 장을 통째로 기울이면 「비스듬한 판」이지 말린
      // 종이가 아니다
      const rows = 6;
      const rh = d.h / rows;
      const curl = (t: number) => -0.0165 * t * t; // t: 0(위) → 1(아래)

      w.part('종이');
      for (let i = 0; i < rows; i++) {
        const t = (i + 0.5) / rows;
        const g = bevelBox(d.w, rh + 0.0012, 0.0009, 0.0003);
        g.rotateX(-0.09 * ((i + 0.5) / rows) ** 1.4);
        g.translate(0, -rh * (i + 0.5), curl(t));
        paintGeometry(g, '#efe9da');
        w.add(g, 'paper', 0.4);
      }

      w.part('인쇄면');
      w.thinSurface(() => {
        for (let i = 0; i < rows; i++) {
          const t = (i + 0.5) / rows;
          const p = facingFront(d.w - 0.004, rh);
          // 각 띠가 인쇄 캔버스의 제 구간만 보게 UV 를 잘라 붙인다
          const uv = p.attributes.uv as THREE.BufferAttribute;
          for (let k = 0; k < uv.count; k++) {
            uv.setY(k, (rows - 1 - i + uv.getY(k)) / rows);
          }
          uv.needsUpdate = true;
          p.rotateX(-0.09 * ((i + 0.5) / rows) ** 1.4);
          p.translate(0, -rh * (i + 0.5), curl(t) - 0.0006);
          w.add(p, 'painted', 0);
        }
      });

      w.part('테이프');
      // 위 두 귀만 살아 있다
      for (const sx of [-1, 1]) {
        const g = tape(0.026, 0.014, sx * 0.16);
        g.translate(sx * (d.w / 2 - 0.014), -0.009, -0.0007);
        w.add(g, 'paper', 0.3);
      }
      // 아래 왼쪽 귀에 떨어진 테이프가 반만 남아 종이에 매달려 있다
      const dead = tape(0.022, 0.012, 0.55);
      dead.translate(-d.w / 2 + 0.012, -d.h + 0.014, curl(0.95) - 0.0009);
      w.add(dead, 'paper', 0.3);
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          // 붉은 테두리 두 줄 — 「금지」 게시물의 정체성이다
          g.strokeStyle = '#8f3126';
          g.lineWidth = 7;
          g.strokeRect(W * 0.06, H * 0.05, W * 0.88, H * 0.9);
          g.lineWidth = 2.5;
          g.strokeRect(W * 0.085, H * 0.072, W * 0.83, H * 0.856);

          // 큰 제목 두 글자 자리 — 획으로만 (서버에 한글 폰트가 없으면 두부가 된다)
          g.strokeStyle = '#8f3126';
          for (let i = 0; i < 2; i++) {
            stroke(g, W * 0.17, H * (0.2 + i * 0.085), W * 0.83, H * (0.2 + i * 0.085), H * 0.05, i * 7);
          }
          // 본문 넉 줄
          g.strokeStyle = 'rgba(38,44,56,0.72)';
          for (let i = 0; i < 4; i++) {
            stroke(
              g,
              W * 0.14,
              H * (0.44 + i * 0.075),
              W * (0.5 + rnd(i * 5) * 0.36),
              H * (0.44 + i * 0.075),
              H * 0.022,
              i * 3,
            );
          }
          // 아래 날짜와 서명 자리
          num(g, '12.16', W * 0.14, H * 0.84, H * 0.05, '#3a4150');
          stroke(g, W * 0.5, H * 0.845, W * 0.86, H * 0.845, H * 0.032, 21);

          // 오래 붙어 있던 종이다. 위 귀 테이프 자국이 누렇게 남았다
          g.fillStyle = 'rgba(176,150,96,0.2)';
          g.fillRect(W * 0.06, 0, W * 0.2, H * 0.06);
          g.fillRect(W * 0.74, 0, W * 0.2, H * 0.06);
        },
        360,
        512,
        '#f2ecdd',
      ),
    }),
  },

  /* ---------------------------------------------------------------- *
   * HS12 — 바닥 끌림흔 · 옷감 눌림
   * ---------------------------------------------------------------- */
  {
    id: 'drag_mark',
    name: '바닥 끌림흔',
    collection: 'sahol',
    category: '단서',
    note:
      'HS12. 침실 바닥에서 붙박이장 쪽으로 난 짧은 끌림흔. **대규모 혈흔이 없다는 것이 단서의 절반이다** — ' +
      '피가 아니라 먼지가 쓸려 나간 자국이라 밝은 띠로 읽혀야 하고, 붉은 것은 아무것도 없어야 한다. ' +
      '끌림 두 줄 사이에 옷감이 눌린 넓은 자국이 있고, 끝나는 쪽에 검은 합성섬유 가닥이 걸려 있다(HS13 과 이어진다). ' +
      '바닥 데칼이라 두께가 없고 y=0 바로 위에 눕는다. 섬유 가닥은 따로 떨어져 있어 clusters 4.',
    dims: { w: 0.62, d: 0.36 },
    clusters: 4,
    build(w, d) {
      w.part('끌림흔');
      w.thinSurface(() => {
        const p = facingUp(d.w, d.d);
        p.translate(0, 0.0012, 0);
        w.add(p, 'painted', 0);
      });

      // 끌림이 끝나는 쪽(+X)에 걸린 검은 합성섬유 세 가닥. 데칼만 있으면 「바닥 무늬」로
      // 읽히고, 이 세 가닥이 있어야 무언가가 지나갔다는 것이 보인다
      w.part('섬유');
      for (let i = 0; i < 3; i++) {
        const bx = d.w * 0.34 + rnd(i * 11) * 0.05;
        const bz = -d.d * 0.16 + i * d.d * 0.16;
        const pts: THREE.Vector3[] = [];
        for (let k = 0; k <= 5; k++) {
          const t = k / 5;
          pts.push(
            new THREE.Vector3(
              bx - t * (0.026 + rnd(i * 3) * 0.02),
              0.0016 + Math.sin(t * Math.PI) * 0.0022,
              bz + Math.sin(t * 5.5 + i) * 0.006,
            ),
          );
        }
        ribbon(w, pts, 0.0011, 0.0007, 'fabric');
      }
    },
    materials: () => ({
      painted: decalFace(
        (g, W, H) => {
          // 캔버스 2D 의 blur 필터를 쓴다. 먼지 자국을 fillRect 로 찍으면 구슬을 꿴 줄처럼
          // 보이고(실제로 그랬다) 데칼 전체가 「바닥 무늬 스티커」가 된다.
          const dust = (a: number) => `rgba(233,223,201,${a})`;
          // 끌림 경로: 왼쪽 붙박이장 쪽에서 오른쪽으로, 아주 살짝 휜다
          const px = (t: number) => W * (0.07 + t * 0.8);
          const py = (t: number) => H * (0.5 + Math.sin(t * 2.1) * 0.035);

          // **좌우를 뒤집어 그린다.** facingUp() 이 PlaneGeometry 를 rotateY(π) 하므로
          // 텍스처의 오른쪽이 월드 -X 로 간다. 뒤집지 않으면 끌림이 멈춘 자리(오른쪽 끝
          // 뭉친 먼지)와 붙박이장이 서로 반대편에 놓여, 「어디서 어디로 끌렸는지」가 거꾸로
          // 읽힌다. 단서 자체가 뒤집히는 것이라 색 문제와 격이 다르다.
          g.save();
          g.translate(W, 0);
          g.scale(-1, 1);

          // 1) 먼지가 얕게 앉은 바탕. 가장자리는 알파로 사라진다
          g.filter = 'blur(26px)';
          g.fillStyle = 'rgba(72,62,48,0.1)';
          g.beginPath();
          g.ellipse(W * 0.48, H * 0.5, W * 0.42, H * 0.36, 0, 0, Math.PI * 2);
          g.fill();

          // 2) 옷감이 눌린 넓은 자국 — 둘레가 흐릿한 한 덩어리여야 한다
          g.filter = 'blur(18px)';
          g.fillStyle = dust(0.2);
          g.beginPath();
          g.ellipse(W * 0.42, H * 0.5, W * 0.2, H * 0.24, 0.05, 0, Math.PI * 2);
          g.fill();
          g.beginPath();
          g.ellipse(W * 0.6, H * 0.52, W * 0.13, H * 0.19, -0.08, 0, Math.PI * 2);
          g.fill();

          // 3) 눌린 자국 안의 옷감 결. 밖으로 새면 마룻바닥 무늬가 되므로 클립한다
          g.save();
          g.beginPath();
          g.ellipse(W * 0.46, H * 0.5, W * 0.24, H * 0.22, 0.03, 0, Math.PI * 2);
          g.clip();
          g.filter = 'blur(2px)';
          g.strokeStyle = dust(0.16);
          g.lineWidth = 1.4;
          for (let i = 0; i < 30; i++) {
            const y = H * (0.24 + i * 0.018);
            g.beginPath();
            g.moveTo(W * 0.18, y);
            g.lineTo(W * 0.76, y + rnd(i) * 3 - 1.5);
            g.stroke();
          }
          g.restore();

          // 4) 끌림 두 줄. 왼쪽이 짙고 오른쪽으로 갈수록 먼지가 덜 쓸렸다
          g.filter = 'blur(6px)';
          g.lineCap = 'round';
          for (const off of [-0.145, 0.135]) {
            for (let i = 0; i < 44; i++) {
              const t = i / 44;
              const t2 = (i + 1.15) / 44;
              g.strokeStyle = dust(0.42 * (1 - t) ** 1.3 + 0.03);
              g.lineWidth = H * (0.062 - t * 0.026) * (0.8 + rnd(i + off * 100) * 0.4);
              g.beginPath();
              g.moveTo(px(t), py(t) + H * off);
              g.lineTo(px(t2), py(t2) + H * off);
              g.stroke();
            }
          }

          // 5) 끌림이 멈춘 자리 — 발이 걸려 먼지가 뭉쳤다
          g.filter = 'blur(11px)';
          g.fillStyle = dust(0.3);
          g.beginPath();
          g.ellipse(W * 0.855, H * 0.49, W * 0.045, H * 0.14, 0.2, 0, Math.PI * 2);
          g.fill();
          g.restore();
          g.filter = 'none';
        },
        640,
        380,
      ),
    }),
  },

  /* ---------------------------------------------------------------- *
   * HS19 — 현관 디지털 도어록
   * ---------------------------------------------------------------- */
  {
    id: 'door_lock',
    name: '현관 도어록',
    collection: 'sahol',
    category: '단서',
    note:
      'HS19. 현관문 바깥면의 디지털 도어록. **파손이 없다는 것이 단서다** — 억지로 연 흔적이 ' +
      '하나도 없어야 하므로 몸체에 흠집·비틀림을 넣지 않는다. 로그는 조사 UI 가 보여주니 3D 는 ' +
      '「멀쩡히 붙어 있는 물건」이기만 하면 된다. **상표를 피하려고 형태를 범용으로 묶었다**: ' +
      '세로 직사각 둥근판 + 숫자 격자 + 곧은 바 손잡이. 원형 다이얼과 곡면 커버는 쓰지 않는다. ' +
      '문에 붙으므로 원점이 상단 부착점이다(hangs + sunk).',
    hangs: true,
    sunk: true,
    dims: { w: 0.076, h: 0.3, t: 0.038 },
    /* 손잡이를 잡아당겨 본다 — 잠겨 있어 몇 mm 딸려 나올 뿐 돌아가지 않는다 (2026-09-16) */
    openable: [{ part: '손잡이', slide: [0, 0, -0.014] }],
    clusters: 1,
    build(w, d) {
      const cy = -d.h / 2;

      // **어두운 슬롯에 어두운 색을 칠하지 마라.** 첫 판은 plastic(#2c2e33) 위에
      // #2c2f34 를 곱해서 몸체가 (0,0,0) 으로 떨어졌다 — 배경(21,24,30)보다 어두워
      // 물건이 통째로 사라지고 알루미늄 손잡이만 막대기처럼 떠 있었다(픽셀로 확인).
      // paintGeometry 는 곱하기라 어둡게만 할 수 있으므로, 밝은 paint(#cdc8be) 를
      // 바탕으로 잡고 곱해서 회탄색으로 내린다.
      w.part('몸체');
      const body = bevelBox(d.w, d.h, d.t, 0.006);
      body.translate(0, cy, -d.t / 2);
      paintGeometry(body, '#aeb4c0');
      w.add(body, 'paint', 0.3);

      // 위쪽 3분의 1 이 키패드 판이다. 살짝 파 넣어 단차를 준다
      w.part('키패드');
      const pad = bevelBox(d.w - 0.012, d.h * 0.42, 0.004, 0.0015);
      pad.translate(0, cy + d.h * 0.24, -d.t - 0.0015);
      paintGeometry(pad, '#8b93a3');
      w.add(pad, 'paint', 0.2);
      w.thinSurface(() => {
        const p = facingFront(d.w - 0.018, d.h * 0.39);
        p.translate(0, cy + d.h * 0.24, -d.t - 0.0038);
        w.add(p, 'screen', 0);
      });

      w.part('손잡이');
      w.movable('손잡이', () => {
      // 곧은 세로 바 — 아래쪽에 서고 위아래 지지대로 몸체에 닿는다.
      // 16mm 정사각 막대는 화면에서 '꼬챙이' 로 읽혔다. 납작하고 넓게 잡는다
      const bar = bevelBox(0.026, d.h * 0.34, 0.013, 0.004);
      bar.translate(0, cy - d.h * 0.2, -d.t - 0.03);
      paintGeometry(bar, '#c6ccd4');
      w.add(bar, 'alu', 0.15);
      for (const sy of [1, -1]) {
        const arm = bevelBox(0.016, 0.014, 0.034, 0.003);
        arm.translate(0, cy - d.h * 0.2 + sy * d.h * 0.15, -d.t - 0.0155);
        paintGeometry(arm, '#c6ccd4');
        w.add(arm, 'alu', 0.15);
      }
      });

      w.part('열쇠 덮개');
      // 비상키 구멍의 미닫이 덮개. 닫혀 있다 — 아무도 비상키를 안 썼다
      const cover = bevelBox(d.w * 0.5, 0.024, 0.003, 0.0012);
      cover.translate(0, cy - d.h * 0.44, -d.t - 0.0012);
      paintGeometry(cover, '#949bA8');
      w.add(cover, 'paint', 0.2);
      const nub = bevelBox(0.014, 0.0035, 0.0025, 0.001);
      nub.translate(0, cy - d.h * 0.44, -d.t - 0.0032);
      w.add(nub, 'alu', 0.1);
    },
    materials: () => ({
      // **발광 화면으로 만들지 않는다.** 첫 판은 litFace(검은 바탕 #0b0e12 + 알파 0.05~0.5)
      // 였는데, 어두운 키패드 판 위에서 숫자가 (0,0,0) 으로 측정됐다 — 격자가 아예 없었다.
      // 게다가 밝게 켜면 「방금 눌렸다」로 읽혀 단서(23:18 이후 조작 없음)와 어긋난다.
      // 그래서 **인쇄면**이다: 눌리지 않은 도어록의 숫자는 빛나는 게 아니라 새겨져 있다.
      screen: printedFace(
        (g, W, H) => {
          const cols = 3;
          const rows = 4;
          const cw = W / cols;
          const ch = (H * 0.86) / rows;
          const top = H * 0.1;
          const roundRect = (x: number, y: number, bw: number, bh: number, r: number) => {
            g.beginPath();
            g.moveTo(x + r, y);
            g.lineTo(x + bw - r, y);
            g.quadraticCurveTo(x + bw, y, x + bw, y + r);
            g.lineTo(x + bw, y + bh - r);
            g.quadraticCurveTo(x + bw, y + bh, x + bw - r, y + bh);
            g.lineTo(x + r, y + bh);
            g.quadraticCurveTo(x, y + bh, x, y + bh - r);
            g.lineTo(x, y + r);
            g.quadraticCurveTo(x, y, x + r, y);
            g.closePath();
          };
          for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
              const x = c * cw + cw * 0.13;
              const y = top + r * ch + ch * 0.1;
              const bw = cw * 0.74;
              const bh = ch * 0.8;
              // 눌리는 판은 몸체보다 밝다 — 이 명암차가 멀리서 '격자' 를 만든다
              g.fillStyle = '#7f8a9b';
              roundRect(x, y, bw, bh, W * 0.045);
              g.fill();
              g.strokeStyle = '#a6b0be';
              g.lineWidth = 2;
              g.stroke();
              // 아래쪽 그림자 한 줄 — 판이 파여 있는 것처럼 보인다
              g.strokeStyle = 'rgba(24,27,33,0.75)';
              g.lineWidth = 3;
              g.beginPath();
              g.moveTo(x + W * 0.03, y + bh);
              g.lineTo(x + bw - W * 0.03, y + bh);
              g.stroke();

              const n = r * cols + c + 1;
              const cx = x + bw / 2;
              const cy2 = y + bh / 2 + H * 0.021;
              if (n <= 9) num(g, String(n), cx, cy2, H * 0.062, '#e6eaf0', 'center');
            }
          }
          // 맨 아랫줄 가운데는 0, 좌우는 확인·취소 도형(언어를 안 탄다)
          const y3 = top + 3 * ch + ch * 0.1;
          const cy3 = y3 + ch * 0.8 / 2;
          num(g, '0', W * 0.5, cy3 + H * 0.021, H * 0.062, '#e6eaf0', 'center');
          g.strokeStyle = '#e6eaf0';
          g.lineWidth = 5;
          g.lineCap = 'round';
          g.beginPath();
          g.moveTo(W * 0.105, cy3);
          g.lineTo(W * 0.15, cy3 + H * 0.016);
          g.lineTo(W * 0.235, cy3 - H * 0.022);
          g.stroke();
          g.beginPath();
          g.moveTo(W * 0.775, cy3 - H * 0.018);
          g.lineTo(W * 0.86, cy3 + H * 0.018);
          g.moveTo(W * 0.86, cy3 - H * 0.018);
          g.lineTo(W * 0.775, cy3 + H * 0.018);
          g.stroke();
          // 상단 잠김 표시등. 점 하나뿐이고 발광하지 않는다 — 대기 상태다
          g.fillStyle = '#3f7f57';
          g.beginPath();
          g.arc(W * 0.5, H * 0.05, W * 0.036, 0, Math.PI * 2);
          g.fill();
        },
        256,
        640,
        '#4d5462',
      ),
    }),
  },

  /* ---------------------------------------------------------------- *
   * FR04 — 재무실 인체감지 센서등
   * ---------------------------------------------------------------- */
  {
    id: 'motion_light',
    name: '인체감지 센서등',
    collection: 'sahol',
    category: '단서',
    note:
      'FR04. 재무실 천장의 인체감지 조명. **꺼져 있어야 한다** — 23:18~00:28 에 활성 기록이 없다는 것이 ' +
      '단서 자체라, 발광 슬롯을 쓰지 않고 무광 유백색 커버로만 만든다. 가운데 볼록한 프레넬 렌즈가 ' +
      '「사람을 감지하는 물건」이라는 표시고, 이게 없으면 그냥 실링라이트다. 천장에 붙으므로 원점이 ' +
      '천장 부착면이다(hangs + sunk).',
    hangs: true,
    sunk: true,
    dims: { r: 0.115, h: 0.062 },
    clusters: 1,
    build(w, d) {
      w.part('천장판');
      const base = lathe(
        [
          [0, 0],
          [d.r * 0.94, 0],
          [d.r * 0.96, -0.006],
          [d.r * 0.9, -0.01],
        ],
        26,
      );
      paintGeometry(base, '#d8d4cb');
      w.add(base, 'alu', 0.25);

      w.part('커버');
      // 완전한 반구는 조명기구로 안 읽힌다. 옆이 곧게 서다 위에서 완만히 도는 접시형
      const prof: [number, number][] = [
        [d.r * 0.9, -0.009],
        [d.r, -0.016],
        [d.r * 0.99, -0.032],
        [d.r * 0.9, -0.046],
        [d.r * 0.66, -0.056],
        [d.r * 0.34, -0.061],
        [0, -d.h],
      ];
      const cov = lathe(prof, 30);
      paintGeometry(cov, '#f6f4ec');
      w.add(cov, 'porcelain', 0.3);

      w.part('센서');
      // 프레넬 렌즈 — 동심 계단이 있어야 렌즈로 보인다
      const lensProf: [number, number][] = [[0, -d.h - 0.0005]];
      for (let i = 0; i < 5; i++) {
        const t = i / 5;
        const r = 0.0055 + t * 0.0125;
        lensProf.push([r, -d.h - 0.0075 + t * 0.0042]);
        lensProf.push([r + 0.0015, -d.h - 0.0052 + t * 0.0042]);
      }
      lensProf.push([0.0215, -d.h + 0.0006]);
      const lens = lathe(lensProf, 22);
      paintGeometry(lens, '#c9ccc6');
      w.add(lens, 'plastic', 0.15);
      const ring = lathe(
        [
          [0.0215, -d.h + 0.0006],
          [0.0255, -d.h + 0.0012],
          [0.0255, -d.h + 0.006],
          [0.0215, -d.h + 0.0072],
        ],
        22,
      );
      paintGeometry(ring, '#8e918c');
      w.add(ring, 'plastic', 0.15);

      w.part('통풍구');
      // 옆면 슬릿 — 매끈한 원반이면 접시가 된다
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2;
        const s = bevelBox(0.006, 0.0022, 0.0018, 0.0006);
        s.rotateY(-a);
        s.translate(Math.sin(a) * d.r * 0.985, -0.024, Math.cos(a) * d.r * 0.985);
        paintGeometry(s, '#a9a69d');
        w.add(s, 'alu', 0.1);
      }
    },
  },

  /* ---------------------------------------------------------------- *
   * WS01 — 연탄난로와 재통
   * ---------------------------------------------------------------- */
  {
    id: 'coal_stove',
    name: '연탄난로',
    collection: 'sahol',
    category: '단서',
    note:
      'WS01 의 난로 몸통. **단서는 난로가 아니라 재받이 안의 탄 종이다** — 재받이는 열리는 부품이라 ' +
      '`stove_ash_pan` 으로 분리했다(2026-09-08). 이 조각은 몸통만이다. 화구 문은 -Z 를 본다.',
    dims: { r: 0.16, h: 0.66 },
    clusters: 1,
    openable: [
      // 화구 문은 왼쪽(-x) 경첩으로 이미 0.42 rad 열려 구워 있다 — 더 열면 화덕 안이 보인다
      { part: '화구 문', swing: 1.1, axis: 'y', pivot: [-0.046, 0.28, -0.173], inside: { w: 0.16, h: 0.22, d: 0.16, at: [0, 0.3, 0] } },
      // 상판 가운데 연탄 투입구 뚜껑 — 들어 올리면 화덕
      { part: '화구 뚜껑', slide: [0, 0.12, 0], inside: { w: 0.16, h: 0.22, d: 0.16, at: [0, 0.3, 0] } },
    ],
    build(w, d) {
      ashHousing(w, d, true);
      w.at(0, ASH_H, 0, () => coalStoveHeater(w, d));
      /* **재통은 이 조각에서 떼어냈다** (2026-09-08, 파이프라인 ②).
       * E20 은 「빼내는 재받이」라 열리는 부품이어야 하는데 몸통과 한 덩어리면 프롭이 못 움직인다.
       * 재·탄 종이는 전부 `stove_ash_pan` 으로 갔다. 배치는 이 난로 + 재받이 둘로 세운다. */
    },
  },

  /* ---------------------------------------------------------------- *
   * 난로 재받이 — **빼내는 서랍**이다 (덮개 종류 `lid` 의 첫 조각).
   * WS01·E20 이 여기 들어간다.
   * ---------------------------------------------------------------- */
  {
    id: 'stove_ash_pan',
    name: '난로 재받이',
    collection: 'sahol',
    category: '단서',
    note:
      'E20(WS01·WS02). 명주원 작업실 난로 **밑에서 앞으로 빼내는** 재받이다. 열리는 물건이라 조각은 ' +
      '「받이 하나」만 만든다 — 난로 몸통은 `coal_stove_bare` 가 따로 서고 이 받이가 미끄러져 나온다 ' +
      '(prop kind `lid`). **손잡이 홈은 −Z(정면)** 에 있어야 한다. +Z 에 뒀더니 뒷면에서만 보였다.\n' +
      '**얕고 넓어야 한다.** 깊으면 탄 종이가 가라앉아 빼내도 안이 안 보인다. 깊이 5.5cm 에 ' +
      '함석 끝을 바깥으로 만 테두리.\n' +
      '**재는 밝은 회색이다.** `coal` 슬롯(#2b2724)에 칠하면 새까매져서 안이 빈 구멍으로 보인다 — ' +
      '`porcelain` 슬롯에 회색을 얹어 가루처럼 밝게 둔다(첫 판이 그래서 실패했다).\n' +
      '**재만 담기면 단서가 없다.** 가장자리만 남고 가운데가 삭은 종이 예닐곱 장이 재 위에 반쯤 서 있고, ' +
      '그중 하나는 흰 부분이 남아 글자를 읽을 여지가 있어야 한다(WS02 복원).\n' +
      '원점은 받이 **바닥 가운데**. 빼내는 축은 −Z 다.',
    dims: { w: 0.3, h: 0.055, d: 0.24, rim: 0.007 },
    /* 받이를 앞(−Z)으로 빼낸다 — 재와 탄 종이가 받이째 딸려 나온다 (2026-09-16) */
    openable: [{ part: '재받이', slide: [0, 0, -0.15] }],
    clusters: 1,
    build(w, d) {
      w.movable('재받이', () => {
      const hw = d.w / 2;
      const hd = d.d / 2;
      const t = 0.004;

      /* ---- 함석 받이. 아연 도금이라 차가운 회색이다 ---- */
      w.part('받이');
      const floor = bevelBox(d.w, t, d.d, 0.001);
      floor.translate(0, t / 2, 0);
      paintGeometry(floor, '#6e6a63');   // 불 밑이라 바닥이 그을렸다
      w.add(floor, 'alu', 0.45);

      for (const [sx, sz] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        const along = sz === 0 ? d.d : d.w;
        const wall = bevelBox(sz === 0 ? t : along, d.h, sz === 0 ? along : t, 0.001);
        wall.translate(sx * (hw - t / 2), d.h / 2, sz * (hd - t / 2));
        paintGeometry(wall, '#b9bcbd');
        w.add(wall, 'alu', 0.4);
        // 말린 테두리 — 함석 끝을 바깥으로 접은 것. 네 변마다 납작한 띠 하나
        const rim = bevelBox(
          sz === 0 ? d.rim * 2 : along + d.rim * 2,
          d.rim,
          sz === 0 ? along + d.rim * 2 : d.rim * 2,
          d.rim * 0.45,
        );
        rim.translate(sx * hw, d.h + d.rim * 0.3, sz * hd);
        paintGeometry(rim, '#c6c9ca');
        w.add(rim, 'alu', 0.35);
      }

      /* ---- 그을음 ----
       * **연탄난로 밑에서 재를 받는 물건인데 새하얬다**(Chris 09-08 「니가 봐바」).
       * 안쪽 벽 아랫동아리와 바닥 가장자리가 검게 절어야 재받이로 읽힌다. 벽 안쪽에 얇은 판을
       * 덧대 아래 60% 를 어둡게 한다 — 벽 자체를 칠하면 바깥까지 검어진다. */
      w.part('그을음');
      for (const [sx, sz] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        const along = sz === 0 ? d.d - t * 2 : d.w - t * 2;
        const soot = bevelBox(sz === 0 ? 0.0016 : along, d.h * 0.62, sz === 0 ? along : 0.0016, 0.0005);
        soot.translate(sx * (hw - t - 0.001), d.h * 0.31, sz * (hd - t - 0.001));
        paintGeometry(soot, '#2a2622');
        w.add(soot, 'alu', 0.2);
      }

      /* ---- 앞판(−Z) 손잡이 ----
       * **튀어나온 상자가 아니라 파인 홈이다.** 처음에 앞으로 튀어나온 판을 붙였더니
       * 검은 스티커를 붙인 것처럼 보였다. 함석을 안으로 눌러 만든 홈이라 위에 차양이 지고
       * 안쪽이 어둡다 — 그 그림자가 「손가락을 거는 자리」를 만든다. */
      w.part('손잡이');
      const gw = d.w * 0.26;
      // 홈 안쪽 벽 (뒤로 들어간 면)
      const inner = bevelBox(gw, d.h * 0.4, 0.003, 0.001);
      inner.translate(0, d.h * 0.5, -hd + 0.008);
      paintGeometry(inner, '#4f4b46');
      w.add(inner, 'alu', 0.2);
      // 위 차양 — 손가락이 걸리는 턱
      const lip = bevelBox(gw + 0.012, 0.004, 0.011, 0.0015);
      lip.translate(0, d.h * 0.72, -hd + 0.004);
      paintGeometry(lip, '#c9cccd');
      w.add(lip, 'alu', 0.3);
      // 아래 턱
      const low = bevelBox(gw + 0.012, 0.004, 0.011, 0.0015);
      low.translate(0, d.h * 0.28, -hd + 0.004);
      paintGeometry(low, '#b4b7b8');
      w.add(low, 'alu', 0.3);

      /* ---- 재 ----
       * **형태로 풀 문제가 아니라 재질로 풀 문제다.** 구 130개를 흩뿌렸더니 자갈이 됐고,
       * 격자를 흔들었더니 헝겊이 됐다(Chris 09-08, 두 번). 가루는 알갱이 하나하나의 형태가 아니라
       * **표면 정체성**이다 — 조각 스킬의 라우팅 규칙대로 재질(`ashSurface`)로 넘긴다.
       * 면은 얌전한 판 하나로 두고 굴곡은 2mm 안쪽. UV 타일 6cm 라 확대해도 알갱이 크기가 안 변한다. */
      w.part('재');
      const inW = d.w - t * 2, inD = d.d - t * 2;
      const ash = new THREE.PlaneGeometry(inW, inD, 10, 9);
      ash.rotateX(-Math.PI / 2);
      const ap = ash.attributes.position;
      for (let i = 0; i < ap.count; i++) {
        const x = ap.getX(i), z = ap.getZ(i);
        const v = (z + inD / 2) / inD;                   // 0 앞 → 1 뒤
        const wob = (rnd(i * 7.3) - 0.5) * 0.0022;
        const edge = Math.min(1, (inW / 2 - Math.abs(x)) / 0.02, (inD / 2 - Math.abs(z)) / 0.02);
        // 앞이 얕고 뒤가 두껍다 — 앞으로 빼내며 앞쪽이 쓸려 나간다
        // **얇게 깔린 층이다.** 9~22mm 로 뒀더니 재가 통째로 선 덩어리(스티로폼 블록)로 보였다
        // (Chris 09-08). 받이 깊이 55mm 의 1/6 이하 — 내려다봐야 보이는 높이로 둔다
        ap.setY(i, t + (0.004 + v * 0.005 + wob * 0.6) * Math.max(0.3, edge));
      }
      ash.computeVertexNormals();
      // **인덱스를 푼다.** MeshWriter 는 슬롯마다 지오메트리를 합치는데 인덱스 있는 것과 없는 것이
      // 섞이면 그 슬롯이 통째로 빠진다 — 재가 새까맣게 보인 것이 그것이었다(메시가 없어 바닥이 보였다)
      const ashG = ash.toNonIndexed();
      // 정점 색은 흰색 — 텍스처 색을 그대로 통과시킨다(곱하기라 흰색이 항등원)
      paintGeometry(ashG, '#ffffff');
      // **두께 없는 면이 정상인 부품** — 규약(CONVENTIONS §4)대로 thinSurface 로 감싸 닫힘 검사에서 뺀다.
      // 재는 받이 바닥에 깔린 층이라 뒷면이 보일 일이 없다. UV 타일 0(가장자리 검댕은 한 번만)
      w.thinSurface(() => w.add(ashG, 'fabric', 0));

      /* ---- 탄 종이 ----
       * **색종이 조각이 되면 안 된다.** 두께 없는 다각형을 눕혔더니 평평한 색면이었다.
       * 한 장을 **두 겹**으로 만든다: 조금 큰 검은 조각(탄 가장자리) 위에 작은 밝은 조각(안 탄 속).
       * 그래야 「가장자리부터 타 들어간 종이」가 된다. 두께를 주고 반쯤 세운다. */
      /** 탄 종이 한 장. **가장자리가 부서져야 종이다** — 칼로 오린 다각형은 색종이다.
       *  꼭짓점 많은 다각형의 반지름을 크게 흔들어 톱니를 만들고, 겹으로 쌓아 두께와 말림을 준다 */
      const burntSheet = (seed: number, size: number, thick: number) => {
        const n = 14 + Math.floor(rnd(seed) * 6);
        const sh = new THREE.Shape();
        for (let k = 0; k < n; k++) {
          const a2 = (k / n) * Math.PI * 2;
          const jag = 0.55 + rnd(seed + k * 3.3) * 0.45;          // 톱니 — 탄 자리가 물러난다
          const r = size * jag;
          const x = Math.cos(a2) * r, y = Math.sin(a2) * r * 0.8;
          if (k === 0) sh.moveTo(x, y); else sh.lineTo(x, y);
        }
        const gg = new THREE.ExtrudeGeometry(sh, { depth: thick, bevelEnabled: false });
        gg.rotateX(-Math.PI / 2);
        const pp = gg.attributes.position;
        for (let k = 0; k < pp.count; k++) pp.setY(k, pp.getY(k) + 1.6 * pp.getX(k) * pp.getX(k));   // 말림
        pp.needsUpdate = true;
        // **UV 를 조각 크기로 정규화한다.** ExtrudeGeometry 의 UV 는 월드 미터 단위라 4cm 조각에는
        // 320px 캔버스의 귀퉁이 한 점만 찍혀 단색이 됐다 — 글자가 매번 안 보인 진짜 원인.
        // 조각의 XZ 경계를 0..1 로 펴야 인쇄면이 조각 전체에 앉는다
        gg.computeBoundingBox();
        const bb = gg.boundingBox!;
        const uv = gg.attributes.uv;
        const sx = bb.max.x - bb.min.x || 1, sz = bb.max.z - bb.min.z || 1;
        for (let k = 0; k < uv.count; k++) uv.setXY(k, (pp.getX(k) - bb.min.x) / sx, (pp.getZ(k) - bb.min.z) / sz);
        uv.needsUpdate = true;
        return gg;
      };
      w.part('탄 종이');
      /* **재에 얹힌 것이지 재 위에 떠서 기운 것이 아니다** (Chris 09-08 「파편이 기울어져 있고」).
       * 60~85° 로 세웠더니 조각이 재 위에 비스듬히 꽂힌 판때기였다. 탄 종이는 가볍고 얇아서
       * 재 위에 거의 눕는다 — 5~20° 만 기울고, 아래쪽 가장자리는 재 속에 묻힌다. 크기도 줄인다.
       * 「옆에서도 보인다」는 세우는 게 아니라 말림(burntSheet 의 곡률)이 만든다. */
      const ashTop = t + 0.007;                              // 재 면 평균 높이
      for (let i = 0; i < 7; i++) {
        const sc = 0.014 + rnd(i * 9) * 0.011;               // 1.4~2.5cm — 손톱만 한 조각들
        const tilt = 0.08 + rnd(i * 7) * 0.28;               // 5~20°
        const yaw = rnd(i * 13) * Math.PI * 2;
        const px = (rnd(i * 5) - 0.5) * (inW - 0.06);
        const pz = (rnd(i * 11) - 0.5) * (inD - 0.05);
        const py = ashTop - 0.0015;                          // 아래 가장자리가 재에 묻힌다
        const outer = burntSheet(i * 31 + 3, sc, 0.0005);
        outer.rotateX(tilt); outer.rotateY(yaw); outer.translate(px, py, pz);
        paintGeometry(outer, i % 3 === 0 ? '#1c1815' : '#2a2420');
        w.add(outer, 'paper', 0.55);
        // 안 탄 속 — 조금 작고 밝다. 두 장 중 하나만(다 밝으면 얼룩무늬가 된다)
        if (i % 2 === 0) {
          const inner = burntSheet(i * 31 + 3, sc * 0.5, 0.0004);
          inner.rotateX(tilt); inner.rotateY(yaw); inner.translate(px, py + 0.0008, pz);
          paintGeometry(inner, '#8a8272');
          w.add(inner, 'paper', 0.5);
        }
      }

      /* ---- 읽히는 한 장 (WS02) ----
       * **글자 자국이 없으면 「복원한다」가 성립하지 않는다.** 크림색 오각형 하나로는
       * 아무것도 안 읽힌다 — 인쇄면을 그려 넣고, 아래 가장자리는 타 들어간 검은 테를 남긴다. */
      w.part('읽히는 조각');
      // 탄 테는 **밑에 깔아** 테두리만 비어져 나오게 한다. 위에 얹으면 인쇄면을 덮는다
      // **앞의 여러 판이 이 줄을 못 고쳤다** — 앵커가 안 맞아 패치가 조용히 빠졌고, 그래서 이 조각만
      // 옛 shard() 로 남아 33° 로 기울고 UV 정규화가 안 돼 글자가 한 번도 안 보였다.
      // 탄 테: 조금 크고 검은 것을 밑에. 거의 눕힌다(7°)
      const charEdge = burntSheet(77, 0.052, 0.0005);
      charEdge.rotateX(0.12); charEdge.rotateY(-0.35);
      charEdge.translate(-0.04, ashTop - 0.001, 0.03);
      paintGeometry(charEdge, '#1d1815');
      w.add(charEdge, 'paper', 0.5);
      // 읽히는 속: 탄 테보다 작고, 곡률이 같으니 1.8mm 만 띄우면 어디서도 안 덮인다.
      // burntSheet 가 UV 를 0..1 로 펴 주므로 인쇄면이 조각 전체에 앉는다
      const readable = burntSheet(77, 0.044, 0.0006);
      readable.rotateX(0.12); readable.rotateY(-0.35);
      readable.translate(-0.04, ashTop + 0.0018, 0.03);
      // **타일 0 + 흰 정점색.** 타일을 주면 MeshWriter 가 UV 를 미터 단위로 다시 깔아 위에서 정규화한
      // 인쇄면 UV 가 날아가고, 정점색을 안 주면 기본 회색이 곱해져 크림색 종이가 갈색이 된다
      paintGeometry(readable, '#ffffff');
      w.add(readable, 'painted', 0);
      });
    },
    /** 읽히는 조각의 인쇄면 — 손글씨 몇 줄과 밑줄. 글자는 조사 UI 가 읽어 준다 */
    materials: () => ({
      // 재 — 알갱이 결은 텍스처가 낸다. 색·거칠기·요철이 같은 표면을 말해야 한다(재질 스킬 규약)
      fabric: ashSurface(),
      painted: printedFace(
        (g, cw, ch) => {
          g.fillStyle = '#ded5bd';
          g.fillRect(0, 0, cw, ch);
          // 손글씨 몇 줄 — 획만 남기고 아래로 갈수록 타서 흐려진다
          g.strokeStyle = '#3a332a';
          g.lineWidth = Math.max(3, cw * 0.03);
          g.lineCap = 'round';
          for (let r = 0; r < 4; r++) {
            const y = ch * (0.2 + r * 0.17);
            let x = cw * 0.14;
            g.globalAlpha = 0.95 - r * 0.12;
            while (x < cw * 0.86) {
              const len = cw * (0.03 + ((r * 7 + x) % 11) / 90);
              g.beginPath();
              g.moveTo(x, y + Math.sin(x * 0.07) * ch * 0.008);
              g.lineTo(x + len, y + Math.cos(x * 0.05) * ch * 0.01);
              g.stroke();
              x += len + cw * 0.022;
            }
          }
          g.globalAlpha = 1;
          // 아래쪽이 탄다 — 갈색에서 검정으로
          const grad = g.createLinearGradient(0, ch * 0.55, 0, ch);
          grad.addColorStop(0, 'rgba(60,45,30,0)');
          grad.addColorStop(0.6, 'rgba(45,33,22,0.75)');
          grad.addColorStop(1, 'rgba(20,16,13,0.95)');
          g.fillStyle = grad;
          g.fillRect(0, ch * 0.55, cw, ch * 0.45);
        },
        320,
        320,
        '#ded5bd',
      ),
    }),
  },

  /* ---------------------------------------------------------------- *
   * 관사용 연탄난로 — 재통 없음
   * ---------------------------------------------------------------- */
  {
    id: 'coal_stove_bare',
    name: '연탄난로(재통 없음)',
    collection: 'sahol',
    category: '가구',
    note:
      '난방기구로만 쓰는 연탄난로. `coal_stove` 와 같은 몸통이지만 **재통이 없다** — ' +
      '재통 안의 탄 종이는 WS01 의 단서라, 단서가 없는 방(관사 임시수사실·신문실)에 두면 ' +
      '헛다리가 된다. 화구 문은 -Z 를 본다. 덩이는 난로 하나뿐이라 clusters 1.',
    dims: { r: 0.16, h: 0.66 },
    clusters: 1,
    openable: [
      { part: '화구 문', swing: 1.1, axis: 'y', pivot: [-0.046, 0.28, -0.173], inside: { w: 0.16, h: 0.22, d: 0.16, at: [0, 0.3, 0] } },
      { part: '화구 뚜껑', slide: [0, 0.12, 0], inside: { w: 0.16, h: 0.22, d: 0.16, at: [0, 0.3, 0] } },
    ],
    build(w, d) {
      ashHousing(w, d, false);   // 재받이 없는 변형 — 앞판까지 막힌 굽. 높이는 coal_stove 와 같다
      w.at(0, ASH_H, 0, () => coalStoveHeater(w, d));
    },
  },

  /* ---------------------------------------------------------------- *
   * PH06 — 사진 전시 포스터
   * ---------------------------------------------------------------- */
  {
    id: 'exhibit_poster',
    mount: 'wall',
    name: '사진 전시 포스터',
    collection: 'sahol',
    category: '단서',
    note:
      'PH06. 송이경 사진실 벽의 옛 개인전 포스터. **연도가 읽혀야 한다** — 암호화 SSD 의 힌트가 ' +
      '「전시 제목 첫 글자 + 연도」라 숫자는 반드시 또렷하고 크게 그린다(제목은 조사 UI 가 읽어준다). ' +
      '액자가 아니라 압정으로 벽에 바로 붙인 종이라, 네 귀 중 오른쪽 아래가 처져 말려야 한다. ' +
      '원점이 붙인 지점이다(hangs + sunk).',
    hangs: true,
    sunk: true,
    dims: { w: 0.5, h: 0.7 },
    clusters: 1,
    /* 처져 말린 아래 세 띠를 들춰 뒤를 본다 (2026-09-17) */
    openable: [{ part: '모서리', swing: 0.7, axis: 'x', pivot: [0, -0.4375, 0] }],
    build(w, d) {
      // 오른쪽 아래로 갈수록 앞으로 나오게 종이를 가로 여덟 띠로 쪼갠다
      const rows = 8;
      const rh = d.h / rows;
      const sag = (t: number) => -0.021 * t * t * t;
      const PEEL = 5;   // 이 띠부터가 처진 귀 — 들추는 부품이 된다

      const row = (i: number) => {
        const t = (i + 0.5) / rows;
        const g = bevelBox(d.w, rh + 0.0015, 0.0008, 0.0003);
        g.rotateZ(-0.035 * t * t);
        g.translate(0, -rh * (i + 0.5), sag(t));
        paintGeometry(g, '#e8e4da');
        w.add(g, 'paper', 0.4);
      };
      const strip = (i: number) => {
        const t = (i + 0.5) / rows;
        const p = facingFront(d.w - 0.003, rh);
        const uv = p.attributes.uv as THREE.BufferAttribute;
        for (let k = 0; k < uv.count; k++) uv.setY(k, (rows - 1 - i + uv.getY(k)) / rows);
        uv.needsUpdate = true;
        p.rotateZ(-0.035 * t * t);
        p.translate(0, -rh * (i + 0.5), sag(t) - 0.0006);
        w.add(p, 'painted', 0);
      };

      w.part('종이');
      for (let i = 0; i < PEEL; i++) row(i);
      w.part('인쇄면');
      w.thinSurface(() => {
        for (let i = 0; i < PEEL; i++) strip(i);
      });

      w.movable('모서리', () => {
        for (let i = PEEL; i < rows; i++) row(i);
        w.thinSurface(() => {
          for (let i = PEEL; i < rows; i++) strip(i);
        });
      });

      w.part('압정');
      // 왼쪽 위·오른쪽 위·왼쪽 아래 셋만 살아 있다. 오른쪽 아래가 빠져서 처졌다
      for (const [sx, sy] of [
        [-1, 0],
        [1, 0],
        [-1, 1],
      ] as [number, number][]) {
        const t = sy;
        const px = sx * (d.w / 2 - 0.018);
        const py = -(sy ? d.h - 0.018 : 0.018);
        const pin = lathe(
          [
            [0, 0],
            [0.0055, -0.0012],
            [0.0055, -0.0032],
            [0.003, -0.0042],
            [0, -0.0042],
          ],
          12,
        );
        pin.rotateX(Math.PI / 2);
        pin.translate(px, py, sag(t) - 0.0022);
        paintGeometry(pin, ['#a8443a', '#3f6f8c', '#c8a23c'][sy + Math.abs(sx) - 1] ?? '#a8443a');
        w.add(pin, 'plastic', 0.1);
      }
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          // 위쪽 3분의 2 는 흑백 사진 한 장. 형태를 그리지 말고 명암 덩어리만 둔다 —
          // 어설픈 형상보다 「인화지 톤」이 사진으로 더 잘 읽힌다
          const ix = W * 0.09;
          const iy = H * 0.08;
          const iw = W * 0.82;
          const ih = H * 0.56;
          const grd = g.createLinearGradient(ix, iy, ix + iw * 0.4, iy + ih);
          grd.addColorStop(0, '#20242a');
          grd.addColorStop(0.55, '#585d63');
          grd.addColorStop(1, '#0e1013');
          g.fillStyle = grd;
          g.fillRect(ix, iy, iw, ih);
          // 눈 덮인 능선 실루엣 — 이 마을의 전시다
          g.fillStyle = '#c9cdd2';
          g.beginPath();
          g.moveTo(ix, iy + ih * 0.72);
          for (let i = 0; i <= 12; i++) {
            const t = i / 12;
            g.lineTo(ix + iw * t, iy + ih * (0.72 - Math.sin(t * 2.6 + 0.4) * 0.16 - rnd(i) * 0.05));
          }
          g.lineTo(ix + iw, iy + ih);
          g.lineTo(ix, iy + ih);
          g.closePath();
          g.fill();
          g.fillStyle = 'rgba(20,24,30,0.55)';
          g.beginPath();
          g.moveTo(ix, iy + ih * 0.86);
          for (let i = 0; i <= 10; i++) {
            const t = i / 10;
            g.lineTo(ix + iw * t, iy + ih * (0.86 + Math.sin(t * 3.4) * 0.05));
          }
          g.lineTo(ix + iw, iy + ih);
          g.lineTo(ix, iy + ih);
          g.closePath();
          g.fill();
          // 인화 테두리
          g.strokeStyle = 'rgba(255,255,255,0.5)';
          g.lineWidth = 5;
          g.strokeRect(ix, iy, iw, ih);

          // 제목 — 획 두 줄. 첫 글자가 힌트지만 3D 면에서는 읽히지 않아도 된다
          g.strokeStyle = '#22262c';
          stroke(g, W * 0.09, H * 0.71, W * 0.68, H * 0.71, H * 0.036, 4);
          stroke(g, W * 0.09, H * 0.765, W * 0.5, H * 0.765, H * 0.028, 11);

          // 연도 — 이것만 진짜 숫자로. 크고 또렷해야 한다
          num(g, '2019', W * 0.91, H * 0.775, H * 0.075, '#8f3126', 'right');

          // 아래 정보 줄과 가는 구분선
          g.strokeStyle = 'rgba(34,38,44,0.35)';
          g.lineWidth = 2;
          g.beginPath();
          g.moveTo(W * 0.09, H * 0.815);
          g.lineTo(W * 0.91, H * 0.815);
          g.stroke();
          g.strokeStyle = 'rgba(34,38,44,0.6)';
          for (let i = 0; i < 3; i++) {
            stroke(
              g,
              W * 0.09,
              H * (0.855 + i * 0.042),
              W * (0.42 + rnd(i * 7) * 0.42),
              H * (0.855 + i * 0.042),
              H * 0.016,
              i * 5,
            );
          }
          // 오래 붙어 있어 위쪽이 볕에 바랬다
          g.fillStyle = 'rgba(196,178,132,0.16)';
          g.fillRect(0, 0, W, H * 0.3);
        },
        512,
        716,
        '#eee9dc',
      ),
    }),
  },

  /* ---------------------------------------------------------------- *
   * EX01 + EX06 — 마을 안내도 (시야선 · 도보시간)
   * ---------------------------------------------------------------- */
  {
    id: 'village_map',
    mount: 'wall',
    name: '마을 안내도',
    collection: 'sahol',
    category: '단서',
    note:
      'EX01·EX06. 마을 안내도 판. **면은 그리지 않는다 — 수첩과 같은 그림 한 장을 붙인다.** ' +
      '`/maps/base.jpg` 는 손으로 그린 원본(scenario/사흘/그림/마을지도_이동시간.png)이고 ' +
      '수첩 지도 탭(client/src/book.ts)이 이미 그것을 쓴다. 벽판에 비슷한 그림을 따로 그렸더니 ' +
      '「조합센터↔한서진 5분」이 나왔는데 정사(03_맵.md 확정표)와 수첩은 **4분**이었다. ' +
      '한 장만 두고 둘 다 그것을 본다.\n' +
      '이 한 장이 단서 둘을 덮는다 — 오르막이 **하나뿐**이라는 것(EX01. 윤가람이 내려오며 ' +
      '올라가는 배정호를 볼 수밖에 없다)과 집 사이 도보시간(EX06. 한서진 집까지 조합센터 4분 · ' +
      '배정호 9분). 그림 오른쪽 아래의 표가 그 둘째다.\n' +
      '알루미늄 프레임에 끼운 판이라 벽에 걸린다(hangs + sunk). **지도면은 그림의 가로세로비' +
      '(1400×1045)에 맞춘다** — 판에 꽉 채우면 가로로 10%% 늘어나 손글씨가 뭉개진다.',
    hangs: true,
    sunk: true,
    dims: { w: 0.68, h: 0.47, t: 0.022 },
    clusters: 1,
    /* 걸이 선을 축으로 판을 들어 올려 뒤를 본다 (2026-09-17) */
    openable: [{ part: '판', swing: 0.85, axis: 'x', pivot: [0, -0.004, 0] }],
    build(w, d) {
      const cy = -d.h / 2;
      const f = 0.018;

      w.movable('판', () => {
      w.part('프레임');
      const side: [number, number, number, number][] = [
        [0, d.h / 2 - f / 2, d.w, f],
        [0, -d.h / 2 + f / 2, d.w, f],
        [-d.w / 2 + f / 2, 0, f, d.h - f * 2],
        [d.w / 2 - f / 2, 0, f, d.h - f * 2],
      ];
      for (const [x, y, bw, bh] of side) {
        const g = bevelBox(bw, bh, d.t, 0.0025);
        g.translate(x, cy + y, -d.t / 2);
        w.add(g, 'alu', 0.2);
      }

      w.part('판');
      // **plastic(#2c2e33) 을 쓰면 안 된다.** paintGeometry 는 곱하기라 어두운 슬롯 위에
      // 밝은 색을 칠해도 내려가기만 한다 — 그림 좌우로 남는 여백이 검은 띠가 됐다.
      // 밝은 paint 를 바탕으로 깔아야 「그림을 끼운 대지」로 읽힌다
      const board = bevelBox(d.w - f, d.h - f, 0.008, 0.0015);
      board.translate(0, cy, -d.t + 0.006);
      paintGeometry(board, '#dcd8cc');
      w.add(board, 'paint', 0.2);

      w.part('지도면');
      w.thinSurface(() => {
        // 그림의 가로세로비(1400x1045)를 지킨다. 판 안쪽 높이에 맞추고 폭을 거기서 낸다 —
        // 남는 좌우 3cm 는 판 바탕이 그대로 보이는 여백이 된다
        const ih = d.h - f * 1.4;
        const p = facingFront(ih * (1400 / 1045), ih);
        p.translate(0, cy, -d.t + 0.0018);
        w.add(p, 'painted', 0);
      });
      });

      w.part('걸이');
      for (const sx of [-1, 1]) {
        const hook = bevelBox(0.02, 0.012, 0.008, 0.002);
        hook.translate(sx * d.w * 0.3, cy + d.h / 2 - 0.004, 0.004);
        w.add(hook, 'steel', 0.1);
      }
    },
    materials: () => ({ painted: imageFace('/maps/base.jpg') }),
  },

  /* ---------------------------------------------------------------- *
   * 장식 — 방을 「사람이 쓰는 곳」으로 만드는 최소 셋
   *
   * 이 레포에는 벽에 거는 조각이 스무 종 있는데 대부분 단서다(게시판·포스터·
   * 족자·달력). **어느 방에나 있는 기본**이 없어서 시선 띠(1.5~2.1m)를 채울
   * 방법이 없었다 — 조합센터 일곱 방의 긴 벽이 전부 빈 판으로 나왔다.
   * 시계·소화기·화분 셋이면 사무실·주방·복도 어디든 벽과 바닥이 산다.
   * ---------------------------------------------------------------- */
  {
    id: 'wall_clock',
    mount: 'wall',
    name: '벽시계',
    collection: 'sahol',
    category: '가구',
    note:
      '어느 사무실에나 있는 둥근 벽시계. **바늘과 테두리 두께**가 시계를 시계로 만든다 — ' +
      '납작한 원판이면 접시다. 유리가 살짝 볼록해 테두리 안쪽에 그늘이 진다. ' +
      '초침은 가늘고 길게, 시침은 짧고 굵게 — 길이가 같으면 시각이 안 읽힌다.',
    hangs: true,
    dims: { r: 0.15, t: 0.045 },
    /* 시계를 걸이에서 들어 올려 뗀다 — 뒷면(배터리·설정축)을 본다 (2026-09-16) */
    openable: [{ part: '시계', slide: [0, 0, -0.07] }],
    build(w, d) {
      w.movable('시계', () => {
      w.part('걸이');
      const hook = new THREE.TorusGeometry(0.008, 0.0016, 6, 12);
      hook.rotateY(Math.PI / 2);
      hook.translate(0, -0.008, 0.004);
      w.add(hook, 'steel', 0.05);

      // 원점은 걸이다. 시계 몸통은 그 아래에 중심이 온다
      w.at(0, -d.r - 0.012, 0, () => {
        w.part('테');
        const rim = lathe(
          [
            [d.r - 0.012, -d.t / 2],
            [d.r, -d.t / 2 + 0.006],
            [d.r, d.t / 2 - 0.004],
            [d.r - 0.02, d.t / 2],
            [d.r - 0.026, d.t / 2 - 0.004],
            [d.r - 0.026, -d.t / 2 + 0.004],
            [d.r - 0.012, -d.t / 2],
          ],
          24,
        );
        rim.rotateX(Math.PI / 2);
        paintGeometry(rim, '#4a4640');
        w.add(rim, 'paint', 0.4);

        w.part('문자판');
        const face = lathe([[0, 0], [d.r - 0.022, 0], [d.r - 0.022, 0.004], [0, 0.004]], 24);
        face.rotateX(-Math.PI / 2);
        // **얼굴은 −Z 다.** 처음엔 부호를 반대로 둬서 바늘이 뒷면으로 튀어나왔다 —
        // 시트의 back 에서 바늘이 보이고 front 에서 안 보였다
        face.translate(0, 0, -d.t / 2 + 0.010);
        paintGeometry(face, '#e8e6df');
        w.add(face, 'paint', 0.35);

        w.part('눈금');
        // 12 개. 세 시간마다 굵게 — 이 차이가 없으면 점을 뿌린 원판이다
        for (let i = 0; i < 12; i++) {
          const a = (i / 12) * Math.PI * 2;
          const big = i % 3 === 0;
          const g = bevelBox(big ? 0.011 : 0.005, big ? 0.028 : 0.017, 0.003, 0.001);
          g.translate(0, d.r - 0.042, 0);
          g.rotateZ(-a);
          g.translate(0, 0, -d.t / 2 + 0.004);
          paintGeometry(g, '#2a2724');
          w.add(g, 'paint', 0.2);
        }

        w.part('바늘');
        // 10시 10분 — 시계 사진의 관습이고, 두 바늘이 겹치지 않아 길이 차가 읽힌다
        const hand = (len: number, wid: number, ang: number, col: string) => {
          const g = bevelBox(wid, len, 0.0035, 0.001);
          g.translate(0, len / 2 - wid * 0.6, 0);
          g.rotateZ(-ang);
          g.translate(0, 0, -d.t / 2 - 0.001);
          paintGeometry(g, col);
          w.add(g, 'paint', 0.15);
        };
        hand(d.r * 0.52, 0.012, (10 / 12) * Math.PI * 2 + 0.17, '#26231f');
        hand(d.r * 0.76, 0.009, (2 / 12) * Math.PI * 2, '#26231f');
        hand(d.r * 0.82, 0.0035, (7 / 12) * Math.PI * 2, '#7d2a24');
        const cap = lathe([[0, 0], [0.011, 0], [0.011, 0.006], [0, 0.006]], 12);
        cap.rotateX(-Math.PI / 2);
        cap.translate(0, 0, -d.t / 2 - 0.001);
        paintGeometry(cap, '#3a3630');
        w.add(cap, 'paint', 0.1);

        w.part('유리');
        // 살짝 볼록. 평면이면 유리가 아니라 뚜껑이다
        const glass = lathe(
          [
            [0, 0.010],
            [d.r * 0.5, 0.0092],
            [d.r - 0.026, 0.004],
          ],
          24,
        );
        glass.rotateX(-Math.PI / 2);
        glass.translate(0, 0, -d.t / 2 - 0.002);
        w.add(glass, 'glass', 0.05);
      });
      });
    },
  },

  {
    id: 'fire_extinguisher',
    nofront: true,
    name: '소화기',
    collection: 'sahol',
    category: '가구',
    note:
      '복도·주방·작업장 벽 밑동에 서 있는 빨간 통. **목이 잘록하고 손잡이가 위로 꺾여** ' +
      '있어야 소화기다 — 곧은 원통이면 산소통이다. 호스가 몸통을 감아 내려오고, ' +
      '바닥에 받침 고리가 있다. 채도를 낮춘 빨강 — 밤 조명 아래 형광 빨강은 튄다.',
    dims: { r: 0.075, h: 0.5 },
    /* 소화기를 들어 올린다 — 호스·노즐째 통으로 들린다 (2026-09-16) */
    openable: [{ part: '소화기', slide: [0, 0.03, -0.04] }],
    build(w, d) {
      w.movable('소화기', () => {
      w.part('몸통');
      const body = lathe(
        [
          [0, 0.012],
          [d.r * 0.92, 0.012],
          [d.r, 0.05],
          [d.r, d.h * 0.72],
          [d.r * 0.72, d.h * 0.86],
          [d.r * 0.3, d.h * 0.92],
          [d.r * 0.3, d.h],
          [0, d.h],
        ],
        18,
      );
      paintGeometry(body, '#7d2a22');
      w.add(body, 'paint', 0.35);

      w.part('밸브');
      const neck = lathe(
        [
          [0, d.h],
          [d.r * 0.34, d.h],
          [d.r * 0.34, d.h + 0.045],
          [d.r * 0.2, d.h + 0.05],
          [0, d.h + 0.05],
        ],
        14,
      );
      paintGeometry(neck, '#4c4a46');
      w.add(neck, 'alu', 0.3);
      // 손잡이 — 위로 꺾인 두 장. 이게 없으면 산소통이다
      for (const [dy, len] of [[0.052, 0.11], [0.03, 0.085]] as const) {
        const lever = bevelBox(len, 0.011, 0.016, 0.003);
        lever.translate(len / 2 - 0.01, d.h + dy, 0);
        paintGeometry(lever, '#4c4a46');
        w.add(lever, 'alu', 0.2);
      }
      w.part('압력계');
      const gauge = lathe([[0, 0], [0.016, 0], [0.016, 0.008], [0, 0.008]], 10);
      gauge.rotateX(Math.PI / 2);
      gauge.rotateY(0.6);
      gauge.translate(-0.028, d.h + 0.03, -0.026);
      paintGeometry(gauge, '#d8d4c8');
      w.add(gauge, 'paint', 0.15);

      w.part('호스');
      /**
       * 몸통을 감아 내려온다. **토막 아홉 개로 했더니 시트에서 점선이 됐다** —
       * 원통 토막은 끝이 안 이어진다. 나선 곡선 하나에 튜브를 씌운다.
       */
      const rr = d.r + 0.007;
      const pts: THREE.Vector3[] = [];
      for (let i = 0; i <= 24; i++) {
        const t = i / 24;
        const a = -0.5 + t * 3.4;
        pts.push(new THREE.Vector3(Math.sin(a) * rr, d.h + 0.02 - t * (d.h * 0.62), Math.cos(a) * rr));
      }
      /* TubeGeometry 는 **양 끝이 뚫린다**(경계 모서리 12개 — 투과 게이트가 잡았다).
       * 단면을 길 따라 밀어내면 끝 캡이 자동으로 닫힌다. */
      const hoseSec = new THREE.Shape();
      hoseSec.absarc(0, 0, 0.008, 0, Math.PI * 2, false);
      const hose = new THREE.ExtrudeGeometry(hoseSec, {
        extrudePath: new THREE.CatmullRomCurve3(pts),
        steps: 40,
        bevelEnabled: false,
        curveSegments: 6,
      });
      paintGeometry(hose, '#2b2724');
      w.add(hose, 'rubber', 0.1);
      w.part('노즐');
      const noz = new THREE.CylinderGeometry(0.011, 0.016, 0.075, 8);
      noz.rotateZ(0.35);
      noz.translate(Math.sin(2.9) * (d.r + 0.02), d.h * 0.34, Math.cos(2.9) * (d.r + 0.02));
      paintGeometry(noz, '#3a3630');
      w.add(noz, 'paint', 0.1);
      });
    },
  },

  {
    id: 'potted_plant',
    glb: '/models/sahol/potted_plant',   // 블렌더 GLB (tools/blender/props.py). 아래 build 는 대체이자 크기 계약
    nofront: true,
    name: '화분',
    collection: 'sahol',
    category: '가구',
    note:
      '사무실 구석의 고무나무 화분. **잎이 줄기에서 어긋나게 나야** 식물이다 — ' +
      '같은 높이에 돌려 붙이면 파라솔이 된다. 잎은 넓고 두꺼운 타원, 끝이 살짝 처진다. ' +
      '흙 위에 마사토가 보이고 화분 밑에 받침 접시가 있다. 겨울이라 잎 몇 장이 누렇다.',
    dims: { r: 0.16, h: 0.28, leaves: 11 },
    /* 화분을 들어 옮긴다 — 받침째 통으로 들린다 (2026-09-16) */
    openable: [{ part: '화분', node: '*', slide: [0, 0, -0.06] }],
    build(w, d) {
      w.part('받침');
      const tray = lathe(
        [
          [0, 0],
          [d.r * 0.92, 0],
          [d.r * 0.95, 0.016],
          [d.r * 0.88, 0.018],
          [d.r * 0.86, 0.004],
          [0, 0.004],
        ],
        18,
      );
      paintGeometry(tray, '#6b5a4c');
      w.add(tray, 'paint', 0.3);

      w.part('화분');
      const pot = lathe(
        [
          [0, 0.018],
          [d.r * 0.62, 0.018],
          [d.r * 0.9, d.h - 0.03],
          [d.r * 0.94, d.h - 0.012],
          [d.r * 0.94, d.h],
          [d.r * 0.86, d.h],
          [d.r * 0.84, d.h - 0.02],
          [d.r * 0.56, 0.028],
          [0, 0.028],
        ],
        18,
      );
      paintGeometry(pot, '#8a6a52');
      w.add(pot, 'earthen', 0.5);

      w.part('흙');
      const soil = lathe([[0, d.h - 0.03], [d.r * 0.84, d.h - 0.035], [d.r * 0.84, d.h - 0.02], [0, d.h - 0.012]], 18);
      paintGeometry(soil, '#3b3128');
      w.add(soil, 'earthen', 0.8);

      w.part('줄기');
      const stem = new THREE.CylinderGeometry(0.007, 0.013, 0.42, 6);
      stem.translate(0, d.h + 0.19, 0);
      paintGeometry(stem, '#4a5236');
      w.add(stem, 'paint', 0.6);

      w.part('잎');
      // **어긋나게.** 같은 높이에 돌려 붙이면 파라솔이 된다
      const hash = (n: number) => {
        const v = Math.sin(n * 91.7 + 47.3) * 43758.5453;
        return v - Math.floor(v);
      };
      for (let i = 0; i < d.leaves; i++) {
        const t = i / (d.leaves - 1);
        const y = d.h + 0.06 + t * 0.34;
        const a = i * 2.39 + hash(i) * 0.5;          // 황금각 — 실제 잎차례
        const len = 0.2 - t * 0.07;
        const droop = 0.55 + t * 0.4 + hash(i + 7) * 0.25;
        const leaf = new THREE.SphereGeometry(len * 0.5, 10, 6);
        leaf.scale(0.42, 0.1, 1.0);
        leaf.translate(0, 0, len * 0.5);
        leaf.rotateX(droop);
        leaf.rotateY(a);
        leaf.translate(0, y, 0);
        // 겨울이라 몇 장은 누렇다
        paintGeometry(leaf, hash(i + 3) > 0.78 ? '#7d7a3a' : '#3f5a34');
        w.add(leaf, 'paint', 0.55);
        // 잎자루
        const pet = new THREE.CylinderGeometry(0.0035, 0.005, len * 0.42, 5);
        pet.rotateX(Math.PI / 2 - droop * 0.7);
        pet.rotateY(a);
        pet.translate(Math.sin(a) * len * 0.1, y - 0.008, Math.cos(a) * len * 0.1);
        paintGeometry(pet, '#4a5236');
        w.add(pet, 'paint', 0.5);
      }
    },
  },
];
